import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { SignIn, SignUp } from "@clerk/react";
import { AuthProvider, Show, useAuth } from "@/lib/auth";
import LocalAuth from "@/pages/LocalAuth";
import { Toaster } from "@/components/ui/sonner";
import { MeProvider, useMe } from "@/context/Me";
import Layout from "@/components/Layout";
import Landing from "@/pages/Landing";
import Story from "@/pages/Story";
import Onboarding from "@/pages/Onboarding";
import ClientHome from "@/pages/ClientHome";
import Diagnose from "@/pages/Diagnose";
import Assistant from "@/pages/Assistant";
import { MyJobs, NewJob } from "@/pages/Jobs";
import JobDetail from "@/pages/JobDetail";
import Board from "@/pages/Board";
import { MyProfile, PublicPro } from "@/pages/Profile";
import { Plans, PaymentSuccess, PaymentCancel } from "@/pages/Plans";
import { Notifications, Reports } from "@/pages/Misc";
import AdminHome from "@/pages/admin/AdminHome";
import AdminData from "@/pages/admin/AdminData";
import AdminSettings from "@/pages/admin/AdminSettings";
import CountryGate from "@/components/CountryGate";
import NearbyStores from "@/components/NearbyStores";

const ClientOnly = ({ children }) => {
  const { me } = useMe();
  return me?.profile_type === "pro" ? <Navigate to="/mural" replace /> : children;
};

const StoresPage = () => (
  <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
    <div className="eyebrow mb-1">Materiais</div>
    <h1 className="mb-6 text-4xl font-black uppercase sm:text-5xl">Lojas perto de você</h1>
    <NearbyStores title="Lojas de materiais" />
  </div>
);

const Spinner = () => <div className="flex min-h-[50vh] items-center justify-center"><div className="h-10 w-10 animate-spin rounded-full border-4 border-[#FFC700] border-t-transparent" /></div>;

function MeError() {
  const { error, refresh } = useMe();
  const { signOut } = useAuth();
  return (
    <div className="mx-auto max-w-md px-4 py-20 text-center" data-testid="me-error">
      <h1 className="text-3xl font-black uppercase">Não conseguimos abrir sua conta</h1>
      <p className="mt-3 text-sm text-slate-400">{error}</p>
      <div className="mt-6 flex justify-center gap-3">
        <button data-testid="me-error-retry" className="btn-hazard" onClick={() => refresh(true)}>Tentar novamente</button>
        <button data-testid="me-error-signout" className="btn-ghost" onClick={async () => { await signOut(); window.location.href = "/"; }}>Sair</button>
      </div>
    </div>
  );
}

function Private({ children, profile = true, admin = false }) {
  const { me, loading, error } = useMe();
  return (
    <>
      <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
      <Show when="signed-in">
        {!me && error && !loading ? <MeError /> : !me ? <Spinner /> :
          admin && me.role !== "admin" ? <Navigate to="/" replace /> :
          profile && !me.profile_type && !admin ? <Navigate to="/cadastro" replace /> : children}
      </Show>
    </>
  );
}

function Home() {
  const { me, loading, error } = useMe();
  return (
    <>
      <Show when="signed-out"><Landing /></Show>
      <Show when="signed-in">
        {!me && error && !loading ? <MeError /> : !me ? <Spinner /> : !me.profile_type ? (me.role === "admin" ? <Navigate to="/admin" replace /> : <Navigate to="/cadastro" replace />)
          : me.profile_type === "pro" ? <Navigate to="/mural" replace /> : <Navigate to="/inicio" replace />}
      </Show>
    </>
  );
}

const AuthWrap = ({ children }) => <div className="flex min-h-[80vh] items-center justify-center px-4 py-10">{children}</div>;
function AuthPage({ up }) {
  const { mode, isSignedIn } = useAuth();
  if (mode === "local") return isSignedIn ? <Navigate to="/" replace /> : <AuthWrap><LocalAuth key={up ? "up" : "in"} initial={up ? "signup" : "signin"} /></AuthWrap>;
  return <AuthWrap>{up ? <SignUp routing="path" path="/sign-up" signInUrl="/sign-in" /> : <SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" withSignUp />}</AuthWrap>;
}

function AppRoutes() {
  const { needsCountry } = useMe();
  if (needsCountry) return <CountryGate />;
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/historia" element={<Story />} />
        <Route path="/sign-in/*" element={<AuthPage />} />
        <Route path="/sign-up/*" element={<AuthPage up />} />
        <Route path="/cadastro" element={<Private profile={false}><Onboarding /></Private>} />
        <Route path="/inicio" element={<Private><ClientOnly><ClientHome /></ClientOnly></Private>} />
        <Route path="/diagnostico" element={<Private profile={false}><Diagnose /></Private>} />
        <Route path="/assistente" element={<Private profile={false}><Assistant /></Private>} />
        <Route path="/lojas" element={<Private profile={false}><StoresPage /></Private>} />
        <Route path="/chamados" element={<Private><MyJobs /></Private>} />
        <Route path="/chamados/novo" element={<Private><NewJob /></Private>} />
        <Route path="/chamados/:id" element={<Private><JobDetail /></Private>} />
        <Route path="/mural" element={<Private><Board /></Private>} />
        <Route path="/perfil" element={<Private><MyProfile /></Private>} />
        <Route path="/profissional/:id" element={<PublicPro />} />
        <Route path="/planos" element={<Private><Plans /></Private>} />
        <Route path="/payment/success" element={<Private><PaymentSuccess /></Private>} />
        <Route path="/payment/cancel" element={<Private><PaymentCancel /></Private>} />
        <Route path="/notificacoes" element={<Private profile={false}><Notifications /></Private>} />
        <Route path="/relatorios" element={<Private><Reports /></Private>} />
        <Route path="/admin" element={<Private admin><AdminHome /></Private>} />
        <Route path="/admin/dados" element={<Private admin><AdminData /></Private>} />
        <Route path="/admin/config" element={<Private admin><AdminSettings /></Private>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

export default function App() {
  useEffect(() => {
    if ("serviceWorker" in navigator) navigator.serviceWorker.register("/sw.js").catch(() => {});
  }, []);
  return (
    <BrowserRouter>
      <AuthProvider>
        <MeProvider>
          <AppRoutes />
          <Toaster richColors position="top-center" theme="dark" offset={84} mobileOffset={{ top: 84 }} />
        </MeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
